import React, { useState } from 'react';
import { ArrowLeft, User, Sliders, Shield, Mail, Building, Phone, PlusCircle, X, BarChart2, Users, BookOpen, Clipboard, CheckCircle, Star, Info, Upload, GitBranch } from 'lucide-react';
import DataSourceModal from './DataSourceModal';
interface SettingsScreenProps {
  onBack: () => void;
}
const SettingsScreen: React.FC<SettingsScreenProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState<'account' | 'configuration'>('account');
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  // Mock user data - in a real app, this would come from your auth system
  const userData = {
    name: 'Alex Johnson',
    email: 'alex.johnson@company.com',
    role: 'Senior Software Engineer',
    department: 'Engineering',
    phone: '+1 (555) 123-4567',
    joinDate: 'March 15, 2020',
    isAdmin: true
  };
  // Initial weights for skill assessment factors
  const [assessmentWeights, setAssessmentWeights] = useState({
    mentoring: 15,
    training: 20,
    projects: 35,
    outcomes: 20,
    peer_review: 10
  });
  // Handle weight change
  const handleWeightChange = (factor: string, value: number) => {
    setAssessmentWeights(prev => ({
      ...prev,
      [factor]: value
    }));
  };
  // Calculate total of all weights
  const totalWeight = Object.values(assessmentWeights).reduce((sum, weight) => sum + weight, 0);
  // Handle import graph
  const handleImportGraph = () => {
    setShowImportModal(true);
  };
  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Here you would handle the file import logic
      alert(`File "${file.name}" selected. Import functionality will be implemented in a future update.`);
      setShowImportModal(false);
    }
  };
  return <div className="w-full min-h-screen flex flex-col bg-gray-50" data-id="element-787">
      <header className="bg-white shadow-sm border-b border-gray-200" data-id="element-788">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-789">
          <div className="flex items-center" data-id="element-790">
            <button onClick={onBack} className="mr-4 p-2 rounded-md hover:bg-gray-100" aria-label="Go back" data-id="element-791">
              <ArrowLeft className="w-5 h-5" data-id="element-792" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800" data-id="element-793">Settings</h1>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6" data-id="element-794">
        <div className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-795">
          <div className="border-b border-gray-200" data-id="element-796">
            <nav className="flex" data-id="element-797">
              <button onClick={() => setActiveTab('account')} className={`px-6 py-4 text-sm font-medium flex items-center ${activeTab === 'account' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-798">
                <User className="w-4 h-4 mr-2" data-id="element-799" />
                Account
              </button>
              {userData.isAdmin && <button onClick={() => setActiveTab('configuration')} className={`px-6 py-4 text-sm font-medium flex items-center ${activeTab === 'configuration' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-800">
                  <Sliders className="w-4 h-4 mr-2" data-id="element-801" />
                  Configuration
                </button>}
            </nav>
          </div>
          <div className="p-6" data-id="element-802">
            {activeTab === 'account' && <div className="space-y-6" data-id="element-803">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-804">
                  Account Details
                </h2>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-805">
                  <div className="flex items-center mb-6" data-id="element-806">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xl mr-4" data-id="element-807">
                      {userData.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div data-id="element-808">
                      <h3 className="text-lg font-medium" data-id="element-809">{userData.name}</h3>
                      <p className="text-sm text-gray-500" data-id="element-810">{userData.role}</p>
                      {userData.isAdmin && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1" data-id="element-811">
                          <Shield className="w-3 h-3 mr-1" data-id="element-812" />
                          Admin
                        </span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-813">
                    <div className="flex items-start" data-id="element-814">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5 mr-2" data-id="element-815" />
                      <div data-id="element-816">
                        <p className="text-sm text-gray-500" data-id="element-817">Email</p>
                        <p className="text-sm font-medium" data-id="element-818">{userData.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-819">
                      <Building className="w-5 h-5 text-gray-400 mt-0.5 mr-2" data-id="element-820" />
                      <div data-id="element-821">
                        <p className="text-sm text-gray-500" data-id="element-822">Department</p>
                        <p className="text-sm font-medium" data-id="element-823">
                          {userData.department}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-824">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5 mr-2" data-id="element-825" />
                      <div data-id="element-826">
                        <p className="text-sm text-gray-500" data-id="element-827">Phone</p>
                        <p className="text-sm font-medium" data-id="element-828">{userData.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-829">
                      <User className="w-5 h-5 text-gray-400 mt-0.5 mr-2" data-id="element-830" />
                      <div data-id="element-831">
                        <p className="text-sm text-gray-500" data-id="element-832">Member Since</p>
                        <p className="text-sm font-medium" data-id="element-833">
                          {userData.joinDate}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-3" data-id="element-834">
                  <h3 className="text-md font-medium text-gray-900" data-id="element-835">
                    Account Actions
                  </h3>
                  <button className="text-sm text-blue-600 hover:text-blue-800 text-left" data-id="element-836">
                    Change password
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-800 text-left" data-id="element-837">
                    Update profile information
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-800 text-left" data-id="element-838">
                    Manage notification preferences
                  </button>
                  <button onClick={handleImportGraph} className="text-sm text-blue-600 hover:text-blue-800 text-left flex items-center" data-id="element-839">
                    <GitBranch className="w-4 h-4 mr-2" data-id="element-840" />
                    Import graph data
                  </button>
                </div>
              </div>}
            {activeTab === 'configuration' && <div className="space-y-6" data-id="element-841">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-842">
                  Admin Configuration
                </h2>
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-843">
                  <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-844">
                    <div data-id="element-845">
                      <h3 className="text-md font-medium text-gray-900" data-id="element-846">
                        Growth Agent Settings
                      </h3>
                      <div className="mt-4 space-y-4" data-id="element-847">
                        <div className="flex items-start" data-id="element-848">
                          <div className="flex items-center h-5" data-id="element-849">
                            <input id="enable-resources" name="enable-resources" type="checkbox" defaultChecked={true} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-850" />
                          </div>
                          <div className="ml-3 text-sm" data-id="element-851">
                            <label htmlFor="enable-resources" className="font-medium text-gray-700" data-id="element-852">
                              Enable external resources
                            </label>
                            <p className="text-gray-500" data-id="element-853">
                              Allow the growth agent to suggest resources
                              outside the organization
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start" data-id="element-854">
                          <div className="flex items-center h-5" data-id="element-855">
                            <input id="enable-connections" name="enable-connections" type="checkbox" defaultChecked={true} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-856" />
                          </div>
                          <div className="ml-3 text-sm" data-id="element-857">
                            <label htmlFor="enable-connections" className="font-medium text-gray-700" data-id="element-858">
                              Enable peer connections
                            </label>
                            <p className="text-gray-500" data-id="element-859">
                              Allow the growth agent to suggest connections with
                              other employees
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-6" data-id="element-860">
                      <div className="flex justify-between items-center mb-4" data-id="element-861">
                        <h3 className="text-md font-medium text-gray-900" data-id="element-862">
                          Data Sources
                        </h3>
                        <button onClick={() => setShowDataSourceModal(true)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-863">
                          <PlusCircle className="w-4 h-4 mr-1" data-id="element-864" />
                          Add Data Source
                        </button>
                      </div>
                      <div className="mt-4 space-y-4" data-id="element-865">
                        <div data-id="element-866">
                          <label htmlFor="corporate-resources" className="block text-sm font-medium text-gray-700" data-id="element-867">
                            Corporate Knowledge Base URL
                          </label>
                          <input type="text" name="corporate-resources" id="corporate-resources" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" defaultValue="https://knowledge.company.com/api/v1" data-id="element-868" />
                        </div>
                        <div data-id="element-869">
                          <label htmlFor="learning-resources" className="block text-sm font-medium text-gray-700" data-id="element-870">
                            Learning Management System API Key
                          </label>
                          <input type="password" name="learning-resources" id="learning-resources" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" defaultValue="••••••••••••••••" data-id="element-871" />
                        </div>
                      </div>
                    </div>
                    {/* New Assessment Weights Section */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-872">
                      <div className="flex justify-between items-center mb-4" data-id="element-873">
                        <h3 className="text-md font-medium text-gray-900" data-id="element-874">
                          Assessment Scoring Weights
                        </h3>
                        <div className="flex items-center text-sm" data-id="element-875">
                          <span className={`font-medium ${totalWeight === 100 ? 'text-green-600' : 'text-red-600'}`} data-id="element-876">
                            Total: {totalWeight}%
                          </span>
                          {totalWeight !== 100 && <div className="ml-2 flex items-center text-red-600" data-id="element-877">
                              <Info className="w-4 h-4 mr-1" data-id="element-878" />
                              <span data-id="element-879">Should equal 100%</span>
                            </div>}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" data-id="element-880">
                        <p className="text-sm text-gray-600 mb-2" data-id="element-881">
                          Adjust the weight of each assessment factor to
                          determine how they contribute to the overall skill
                          level calculation.
                        </p>
                      </div>
                      <div className="space-y-4" data-id="element-882">
                        {/* Projects */}
                        <div className="flex flex-col space-y-2" data-id="element-883">
                          <div className="flex items-center justify-between" data-id="element-884">
                            <div className="flex items-center" data-id="element-885">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-886">
                                <Clipboard className="w-4 h-4 text-green-600" data-id="element-887" />
                              </div>
                              <label htmlFor="weight-projects" className="block text-sm font-medium text-gray-700" data-id="element-888">
                                Project Experience
                              </label>
                            </div>
                            <span className="text-sm font-medium" data-id="element-889">
                              {assessmentWeights.projects}%
                            </span>
                          </div>
                          <input type="range" id="weight-projects" min="0" max="100" step="5" value={assessmentWeights.projects} onChange={e => handleWeightChange('projects', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-890" />
                          <p className="text-xs text-gray-500" data-id="element-891">
                            Weight given to practical application in real
                            projects
                          </p>
                        </div>
                        {/* Training */}
                        <div className="flex flex-col space-y-2" data-id="element-892">
                          <div className="flex items-center justify-between" data-id="element-893">
                            <div className="flex items-center" data-id="element-894">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-895">
                                <BookOpen className="w-4 h-4 text-blue-600" data-id="element-896" />
                              </div>
                              <label htmlFor="weight-training" className="block text-sm font-medium text-gray-700" data-id="element-897">
                                Training & Classes
                              </label>
                            </div>
                            <span className="text-sm font-medium" data-id="element-898">
                              {assessmentWeights.training}%
                            </span>
                          </div>
                          <input type="range" id="weight-training" min="0" max="100" step="5" value={assessmentWeights.training} onChange={e => handleWeightChange('training', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-899" />
                          <p className="text-xs text-gray-500" data-id="element-900">
                            Weight given to formal education and certifications
                          </p>
                        </div>
                        {/* Outcomes */}
                        <div className="flex flex-col space-y-2" data-id="element-901">
                          <div className="flex items-center justify-between" data-id="element-902">
                            <div className="flex items-center" data-id="element-903">
                              <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3" data-id="element-904">
                                <CheckCircle className="w-4 h-4 text-yellow-600" data-id="element-905" />
                              </div>
                              <label htmlFor="weight-outcomes" className="block text-sm font-medium text-gray-700" data-id="element-906">
                                Business Outcomes
                              </label>
                            </div>
                            <span className="text-sm font-medium" data-id="element-907">
                              {assessmentWeights.outcomes}%
                            </span>
                          </div>
                          <input type="range" id="weight-outcomes" min="0" max="100" step="5" value={assessmentWeights.outcomes} onChange={e => handleWeightChange('outcomes', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-908" />
                          <p className="text-xs text-gray-500" data-id="element-909">
                            Weight given to measurable impact on business goals
                          </p>
                        </div>
                        {/* Mentoring */}
                        <div className="flex flex-col space-y-2" data-id="element-910">
                          <div className="flex items-center justify-between" data-id="element-911">
                            <div className="flex items-center" data-id="element-912">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3" data-id="element-913">
                                <Users className="w-4 h-4 text-purple-600" data-id="element-914" />
                              </div>
                              <label htmlFor="weight-mentoring" className="block text-sm font-medium text-gray-700" data-id="element-915">
                                Mentoring Activities
                              </label>
                            </div>
                            <span className="text-sm font-medium" data-id="element-916">
                              {assessmentWeights.mentoring}%
                            </span>
                          </div>
                          <input type="range" id="weight-mentoring" min="0" max="100" step="5" value={assessmentWeights.mentoring} onChange={e => handleWeightChange('mentoring', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-917" />
                          <p className="text-xs text-gray-500" data-id="element-918">
                            Weight given to knowledge sharing and teaching
                            others
                          </p>
                        </div>
                        {/* Peer Review */}
                        <div className="flex flex-col space-y-2" data-id="element-919">
                          <div className="flex items-center justify-between" data-id="element-920">
                            <div className="flex items-center" data-id="element-921">
                              <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center mr-3" data-id="element-922">
                                <Star className="w-4 h-4 text-pink-600" data-id="element-923" />
                              </div>
                              <label htmlFor="weight-peer_review" className="block text-sm font-medium text-gray-700" data-id="element-924">
                                Peer Review
                              </label>
                            </div>
                            <span className="text-sm font-medium" data-id="element-925">
                              {assessmentWeights.peer_review}%
                            </span>
                          </div>
                          <input type="range" id="weight-peer_review" min="0" max="100" step="5" value={assessmentWeights.peer_review} onChange={e => handleWeightChange('peer_review', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-926" />
                          <p className="text-xs text-gray-500" data-id="element-927">
                            Weight given to feedback from colleagues and
                            managers
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-100 text-sm text-blue-800 flex items-start" data-id="element-928">
                        <BarChart2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-929" />
                        <p data-id="element-930">
                          These weights determine how different assessment
                          factors contribute to skill level calculations across
                          the organization. Changes will affect all future skill
                          assessments.
                        </p>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-6" data-id="element-931">
                      <h3 className="text-md font-medium text-gray-900" data-id="element-932">
                        User Management
                      </h3>
                      <div className="mt-4" data-id="element-933">
                        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-934">
                          Manage User Access
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" data-id="element-935">
                    <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-936">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </main>
      {/* Data Source Modal */}
      {showDataSourceModal && <DataSourceModal onClose={() => setShowDataSourceModal(false)} data-id="element-937" />}
      {/* Import Graph Modal */}
      {showImportModal && <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center" data-id="element-938">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowImportModal(false)} data-id="element-939"></div>
          <div className="relative bg-white rounded-lg max-w-md w-full mx-auto p-6 shadow-xl" data-id="element-940">
            <div className="flex justify-between items-center mb-4" data-id="element-941">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-942">
                Import Graph Data
              </h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-943">
                <X className="w-5 h-5" data-id="element-944" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-945">
              <p className="text-sm text-gray-500" data-id="element-946">
                Upload a JSON file containing your graph data. This will be
                merged with your existing data.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center" data-id="element-947">
                <Upload className="w-8 h-8 text-gray-400 mb-2" data-id="element-948" />
                <p className="text-sm text-gray-500 mb-2" data-id="element-949">
                  Drag and drop your file here, or click to browse
                </p>
                <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer" data-id="element-950">
                  Browse Files
                  <input type="file" className="hidden" accept=".json" onChange={handleFileSelect} data-id="element-951" />
                </label>
              </div>
              <div className="text-xs text-gray-500" data-id="element-952">
                <p data-id="element-953">Supported format: JSON</p>
                <p data-id="element-954">Maximum file size: 10MB</p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default SettingsScreen;